import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";

const navLinks = [
  { to: "/", label: "Αρχική", sub: "Καλωσόρισες στο GKLed" },
  { to: "/artworks", label: "Artworks", sub: "Φώτισε τις αναμνήσεις" },
  { to: "/project", label: "Η ιδέα σου", sub: "Custom project" },
  { to: "/contact", label: "Επικοινωνία", sub: "Μίλα μαζί μας" },
];

export const Header = () => {
  const { count, setDrawerOpen } = useCart();
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Lock body scroll while menu open
  React.useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading font-black text-2xl tracking-tight text-white"
            data-testid="brand-logo-link"
          >
            <span className="text-[#B026FF] neon-glow-pink">GK</span>
            <span>Led</span>
          </Link>

          <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive ? "text-[#B026FF]" : "text-zinc-300 hover:text-white"
                  }`
                }
                data-testid={`nav-link-${l.label}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              className="relative p-3 rounded-full border border-white/10 hover:border-[#B026FF] hover:text-[#B026FF] transition-colors"
              data-testid="header-cart-button"
              aria-label="Καλάθι"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-[#B026FF] text-black text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center neon-box-glow-pink"
                  data-testid="header-cart-count"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="p-3 rounded-full border border-white/10 hover:border-[#B026FF] hover:text-[#B026FF] transition-colors"
              onClick={() => setMenuOpen(true)}
              data-testid="menu-toggle"
              aria-label="Μενού"
            >
              <Menu size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      {/* SIDE DRAWER MENU */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
              data-testid="menu-backdrop"
            />
            {/* Drawer */}
            <motion.aside
              key="menu-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.32, 0.72, 0, 1], duration: 0.42 }}
              className="fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-[#070007] border-l border-[#B026FF]/20 overflow-y-auto"
              data-testid="menu-drawer"
            >
              {/* Ambient glow */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 80% 40% at 100% 0%, rgba(176,38,255,0.18), transparent 60%)",
                }}
              />

              <div className="relative flex items-center justify-between px-6 py-6 border-b border-white/5">
                <div>
                  <div className="text-xs tracking-[0.3em] uppercase text-[#B026FF] font-bold">
                    Menu
                  </div>
                  <div className="font-heading text-xl font-black mt-1">
                    <span className="text-[#B026FF] neon-glow-pink">GK</span>Led
                  </div>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-3 rounded-full border border-white/10 hover:border-[#B026FF] hover:text-[#B026FF] transition-colors"
                  data-testid="menu-close"
                  aria-label="Κλείσιμο"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="relative px-3 py-4">
                <div className="px-3 text-[11px] tracking-[0.3em] uppercase text-zinc-600 font-bold mb-3">
                  Κατηγορίες
                </div>
                <nav className="flex flex-col" data-testid="menu-nav">
                  {navLinks.map((l, i) => (
                    <motion.div
                      key={l.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 + i * 0.05, duration: 0.3 }}
                    >
                      <NavLink
                        to={l.to}
                        end={l.to === "/"}
                        onClick={() => setMenuOpen(false)}
                        className={({ isActive }) =>
                          `group flex items-center justify-between gap-4 px-4 py-4 rounded-xl border transition-all ${
                            isActive
                              ? "border-[#B026FF] bg-[#B026FF]/10 text-white"
                              : "border-transparent hover:border-white/10 hover:bg-white/[0.03] text-zinc-200"
                          }`
                        }
                        data-testid={`menu-link-${l.label}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-bold text-lg truncate">
                            {l.label}
                          </div>
                          <div className="text-xs text-zinc-500 truncate mt-0.5">
                            {l.sub}
                          </div>
                        </div>
                        <ArrowRight
                          size={18}
                          className="text-zinc-500 group-hover:text-[#B026FF] group-hover:translate-x-1 transition-all"
                        />
                      </NavLink>
                    </motion.div>
                  ))}
                </nav>
              </div>

              <div className="relative px-6 py-5 mt-4 border-t border-white/5 text-xs text-zinc-500">
                <div className="flex items-center justify-between">
                  <span>Premium flex LED · Χειροποίητα</span>
                  <span className="text-[#B026FF]">gkled.gr</span>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
